import { useEffect, useState } from 'react'

import { Card, Row, Input, Button } from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import { useAuth, useLoading, useModal } from 'hooks'
import {
  EfetivoLogicoServices,
  ReqGetAllContracts,
  ResGetAllContracts,
  EffectiveTableData,
  ReqEffectiveByContract
} from 'services/Supplier/LogicalEffective'
import { COLORS, FORMAT, SPACING } from 'utils'

import { changeTabProps } from '../LogicalEffectiveRegister'
import { Collapse } from './components'
import LegendasEfetivoLogico from '../../../../Supplier/Effective/LogicalEffectiveRegister/SearchRegisterEffectives/components/LegendasEfetivo'


// Nova interface para combinar dados do contrato com seus efetivos
interface ContractWithEffectives extends ResGetAllContracts {
  effectiveList: EffectiveTableData[]
  pageCount: number
}

interface ISearchRegisterEffectives {
  handleChangeTab: (props: changeTabProps) => void
  activeContract: string
  reset?: boolean
  setReset?: () => void
}

const SearchRegisterEffectives: React.FC<ISearchRegisterEffectives> = ({
  handleChangeTab,
  activeContract,
  reset,
  setReset
}) => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [searchWord, setSearchWord] = useState('')
  // O estado agora armazena a lista de contratos JUNTO com os efetivos da primeira página
  const [listContracts, setListContracts] = useState<ContractWithEffectives[]>([])
  const { user } = useAuth()

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchWord(FORMAT.unMask(event.target.value))

  // NOVA FUNÇÃO PRINCIPAL: Busca contratos e depois busca os efetivos de cada um sequencialmente
  const fetchContractsAndEffectives = async (searchTerm: string) => {
    setSearchWord(searchTerm);
    setLoading('loading'); // bloqueia a tela

    try {
      const contractsRequest: ReqGetAllContracts = {
        idUsuario: user.IdUsuario,
        isContraparte: true,
        pesquisaGeral: searchTerm
      };

      const response = await EfetivoLogicoServices().getPesquisaContratos(contractsRequest);

      // ✅ VALIDAÇÃO ADICIONADA
      if (!response || !response.data || !Array.isArray(response.data)) {
        setListContracts([]);
        setLoading('idle');
        return;
      }

      const contracts = response.data;

      // ✅ VALIDAÇÃO: Se não houver contratos, limpa a lista
      if (contracts.length === 0) {
        setListContracts([]);
        setLoading('idle');
        return;
      }

      const contractsWithDetails: ContractWithEffectives[] = [];

      for (const contract of contracts) {
        // ✅ VALIDAÇÃO: Verifica se o contrato tem cdContrato
        if (!contract || !contract.cdContrato) {
          continue; // Pula para o próximo contrato
        }

        const effectiveRequest: ReqEffectiveByContract = {
          cdContrato: contract.cdContrato,
          idUsuario: user.IdUsuario,
          pageNumber: 1,
          pageSize: 15,
          isContraparte: true,
          pesquisaGeral: searchTerm
        };

        try {
          const effectiveResponse = await EfetivoLogicoServices().getEfetivosporContrato(effectiveRequest);

          // ✅ VALIDAÇÃO: Verifica se a resposta dos efetivos é válida
          const effectiveData = effectiveResponse?.data || { items: [], pageCount: 0 };

          // Adiciona os dados do contrato + efetivos
          const contractWithEffectives: ContractWithEffectives = {
            ...contract,
            effectiveList: effectiveData.items || [],
            pageCount: effectiveData.pageCount || 0
          };

          contractsWithDetails.push(contractWithEffectives);

          // Atualiza a tela **incrementalmente**
          setListContracts([...contractsWithDetails]);

        } catch (error) {
          console.error(`Falha ao buscar efetivos para o contrato ${contract.cdContrato}:`, error);
          contractsWithDetails.push({
            ...contract,
            effectiveList: [],
            pageCount: 0
          });
          setListContracts([...contractsWithDetails]);
        }

        // Delay de 250 milisegundos antes do próximo contrato
        await new Promise(resolve => setTimeout(resolve, 250));
      }

    } catch (error) {
      showModal({
        hideButtonCancel: true,
        message:
          (error as any).mensagem ||
          (error as any).message ||
          'Não foi possível obter a lista de contratos',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      });
      setListContracts([]); // ✅ Limpa a lista em caso de erro
    } finally {
      setLoading('idle'); // desbloqueia a tela só no final
    }
  };

  useEffect(() => {
    if (reset) {
      fetchContractsAndEffectives('')
      setReset?.()
    }
  }, [reset])

  useEffect(() => {
    fetchContractsAndEffectives('')
  }, [])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <Row justifyContent="space-between">
          <div style={{ display: 'flex', gap: '10px' }}>
            <LegendasEfetivoLogico status='Bloqueado' />
            <LegendasEfetivoLogico status='Liberado' />
            <LegendasEfetivoLogico status='Pendente' />
            <LegendasEfetivoLogico status='Demitido' />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Input
              label="Pesquisa Geral"
              placeholder="Pesquisar"
              value={searchWord}
              onChange={handleChangeSearch}
              onClickIcon={() => fetchContractsAndEffectives(searchWord)}
              inputIcon={<MagnifyingGlassIcon />}
              marginRight={SPACING.spacing5}
              marginBottom={SPACING.spacing10}
              width={SPACING.spacing300}
            />
            <Button
              text="Limpar"
              variantButton="text"
              onClick={() => { fetchContractsAndEffectives('') }}
            />
          </div>
        </Row>
        <Collapse
          activeContract={activeContract}
          collapses={listContracts}
          handleChangeTab={handleChangeTab}
          pesquisaGeral={searchWord}
        />
      </Card>
    </>
  )
}

export default SearchRegisterEffectives